// client/src/pwa/pwaCore.js — the BREC app's install and update logic, as plain functions.
//
// No React and no globals: every function takes what it needs (user agent, the
// registration, navigator.serviceWorker, a reload function), so scripts/test-pwa.js
// can run them with fakes (it loads this file by stripping `export`).

export const SW_URL = '/sw.js';
export const SW_SCOPE = '/';
// The client portal's old worker, retired when the whole site got one (see /sw.js).
export const LEGACY_SCOPES = ['/client-portal/'];

/** iPhone, iPad or iPod — including iPadOS, which reports itself as a Mac with touch. */
export function isIos(ua, platform, maxTouchPoints) {
    const u = String(ua || '');
    if (/iphone|ipad|ipod/i.test(u)) return true;
    return /mac/i.test(String(platform || '')) && Number(maxTouchPoints || 0) > 1;
}

/** Safari on iOS (only Safari can "Add to Home Screen" on older iOS; other iOS browsers say so). */
export function isIosSafari(ua, platform, maxTouchPoints) {
    const u = String(ua || '');
    return isIos(u, platform, maxTouchPoints) && /safari/i.test(u) && !/crios|fxios|edgios|opios|gsa\/|instagram|fban|fbav/i.test(u);
}

/** Already running as the installed app? */
export function isStandalone(displayModeStandalone, navigatorStandalone) {
    return !!displayModeStandalone || navigatorStandalone === true;
}

/**
 * Which install help to show:
 *   'installed' — running as the app already: show nothing
 *   'prompt'    — the browser offered an install prompt (Android Chrome, desktop Chrome/Edge)
 *   'ios'       — iPhone/iPad Safari: show "Share → Add to Home Screen"
 *   'none'      — nothing to offer (e.g. Firefox desktop, or iOS in another browser)
 */
export function installMode({ ua, platform, maxTouchPoints, standalone, hasPrompt }) {
    if (standalone) return 'installed';
    if (hasPrompt) return 'prompt';
    if (isIosSafari(ua, platform, maxTouchPoints)) return 'ios';
    return 'none';
}

/** Is this registration the retired client-portal worker? */
export function isLegacyRegistration(scope, origin) {
    const s = String(scope || '');
    const path = origin && s.startsWith(origin) ? s.slice(origin.length) : s.replace(/^https?:\/\/[^/]+/i, '');
    return LEGACY_SCOPES.includes(path);
}

/**
 * Call onReady(worker) when a NEW version has installed and is waiting. Never on the
 * very first install (no controller yet = nothing old to replace).
 */
export function watchForUpdate(reg, sw, onReady) {
    if (!reg) return;
    const hasController = () => !!(sw && sw.controller);
    if (reg.waiting && hasController()) onReady(reg.waiting);
    reg.addEventListener('updatefound', () => {
        const worker = reg.installing;
        if (!worker) return;
        worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && hasController()) onReady(worker);
        });
    });
}

/**
 * The Reload button: tell the waiting worker to take over, and reload the page once
 * when it has (controllerchange). A second click, or a second controllerchange, does
 * not reload twice.
 */
export function applyUpdate(worker, sw, reload) {
    if (!worker || !sw) { reload(); return; }
    let done = false;
    sw.addEventListener('controllerchange', () => {
        if (done) return;
        done = true;
        reload();
    });
    worker.postMessage({ type: 'SKIP_WAITING' });
}

/** How often an open app asks the server for a new version (and on every return to it). */
export const UPDATE_CHECK_MS = 60 * 60 * 1000;
